/**
 * Demo mode: fabricates realistic card purchases so the whole
 * detect → round-up → bridge → swap → celebrate loop runs without a live
 * Gnosis Pay card. Produces DetectedSpend objects identical in shape to the
 * real scanner's output, so the pipeline cannot tell the difference.
 */
import type { DetectedSpend, Hex } from "./types";

const MERCHANTS: { name: string; min: number; max: number }[] = [
  { name: "Tesco", min: 312, max: 3450 },
  { name: "Costa Coffee", min: 265, max: 745 },
  { name: "Pret A Manger", min: 399, max: 1250 },
  { name: "TfL Travel", min: 180, max: 880 },
  { name: "Sainsbury's", min: 420, max: 5210 },
  { name: "Greggs", min: 130, max: 640 },
  { name: "Amazon", min: 499, max: 8999 },
  { name: "Boots", min: 250, max: 2799 },
];

let demoCounter = 0;

function pseudoHash(seed: string): Hex {
  let h = 0n;
  for (const c of seed) h = (h * 131n + BigInt(c.charCodeAt(0))) % (1n << 256n);
  return `0x${h.toString(16).padStart(64, "0")}` as Hex;
}

/** One random plausible purchase, occasionally an exact-pound amount. */
export function simulatePurchase(): DetectedSpend {
  demoCounter += 1;
  const m = MERCHANTS[Math.floor(Math.random() * MERCHANTS.length)];
  let amountPence = m.min + Math.floor(Math.random() * (m.max - m.min));
  if (Math.random() < 0.12) amountPence = Math.round(amountPence / 100) * 100 || 100;
  return {
    txHash: pseudoHash(`demo-spend-${demoCounter}-${Date.now()}-${Math.random()}`),
    logIndex: 0,
    blockNumber: 40_000_000 + demoCounter,
    timestamp: Date.now(),
    merchant: m.name,
    amountPence,
    currency: "GBP",
    kind: "card_spend",
  };
}

/** The BRIEF's canonical example: £3.42 coffee → 58p round-up. */
export function simulateBriefExample(): DetectedSpend {
  demoCounter += 1;
  return {
    txHash: pseudoHash(`demo-brief-${demoCounter}-${Date.now()}`),
    logIndex: 0,
    blockNumber: 40_000_000 + demoCounter,
    timestamp: Date.now(),
    merchant: "Costa Coffee",
    amountPence: 342,
    currency: "GBP",
    kind: "card_spend",
  };
}
